import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UnifiedProductMovementItem } from 'src/typings';
import {
  AdjustStocktakingV2ReportRowDto,
  CreateStocktakingV2Dto,
  DiscardStocktakingV2ShelfDto,
  HeartbeatStocktakingV2ShelfDto,
  JoinStocktakingV2ParticipantDto,
  LockStocktakingV2ShelfDto,
  SearchStocktakingV2Dto,
  StartStocktakingV2ShelfDto,
  StocktakingV2Doc,
  StocktakingV2HistoryRow,
  StocktakingV2ListItem,
  StocktakingV2ReportRow,
  StocktakingV2ShelfSession,
  StocktakingV2SocketEvent,
  SubmitEmptyShelvesBulkStocktakingV2Dto,
  SubmitStocktakingV2ShelfDto
} from './stocktaking-v2.types';

@Injectable({
  providedIn: 'root'
})
export class StocktakingV2Service {
  readonly createdGW = this.socket.fromEvent<StocktakingV2SocketEvent>('stocktaking_v2_created');
  readonly changedGW = this.socket.fromEvent<StocktakingV2SocketEvent>('stocktaking_v2_changed');
  readonly deletedGW = this.socket.fromEvent<StocktakingV2SocketEvent>('stocktaking_v2_deleted');
  readonly shelfChangedGW = this.socket.fromEvent<StocktakingV2SocketEvent>('stocktaking_v2_shelf_changed');
  readonly participantChangedGW = this.socket.fromEvent<StocktakingV2SocketEvent>(
    'stocktaking_v2_participant_changed'
  );
  readonly reportChangedGW = this.socket.fromEvent<StocktakingV2SocketEvent>(
    'stocktaking_v2_report_changed'
  );

  constructor(private readonly http: HttpClient, private readonly socket: Socket) {}

  create(payload: CreateStocktakingV2Dto) {
    return this.http
      .post<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/create`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  search(payload: SearchStocktakingV2Dto) {
    return this.http
      .post<{ data: StocktakingV2ListItem[]; length: number }>(
        `${environment.BACKEND}/stocktaking-v2/search`,
        payload
      )
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((item) => this.mapListItem(item))
        }))
      );
  }

  getOne(id: number) {
    return this.http
      .get<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/${id}`)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  getReport(id: number) {
    return this.http.get<StocktakingV2ReportRow[]>(`${environment.BACKEND}/stocktaking-v2/report/${id}`);
  }

  adjustReportRow(payload: AdjustStocktakingV2ReportRowDto) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/report/adjust-row`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  printReport(id: number) {
    return this.http.get(`${environment.BACKEND}/stocktaking-v2/report/${id}/print`, {
      responseType: 'blob'
    });
  }

  getHistory(id: number) {
    return this.http
      .get<StocktakingV2HistoryRow[]>(`${environment.BACKEND}/stocktaking-v2/history/${id}`)
      .pipe(
        map((rows) =>
          rows.map((row) => ({
            ...row,
            date: row.date ? new Date(row.date) : row.date
          })) as UnifiedProductMovementItem[]
        )
      );
  }

  joinParticipant(payload: JoinStocktakingV2ParticipantDto) {
    return this.http
      .post<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/participant/join`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  lockShelf(payload: LockStocktakingV2ShelfDto) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/shelf/lock`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  startShelfSession(payload: StartStocktakingV2ShelfDto) {
    return this.http
      .put<StocktakingV2ShelfSession>(`${environment.BACKEND}/stocktaking-v2/shelf/start`, payload)
      .pipe(
        map((session) => ({
          ...session,
          doc: this.mapDoc(session.doc),
          items: Array.isArray(session.items) ? session.items : []
        }))
      );
  }

  heartbeat(payload: HeartbeatStocktakingV2ShelfDto) {
    return this.http.put<{ ok: true }>(`${environment.BACKEND}/stocktaking-v2/shelf/heartbeat`, payload);
  }

  submitShelf(payload: SubmitStocktakingV2ShelfDto) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/shelf/submit`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  submitEmptyShelvesBulk(payload: SubmitEmptyShelvesBulkStocktakingV2Dto) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/shelf/submit-empty-bulk`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  discardShelf(payload: DiscardStocktakingV2ShelfDto) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/shelf/discard`, payload)
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  resumeCounting(id: number) {
    return this.http.put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/resume-counting/${id}`, {})
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  finalize(id: number) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/finalize/${id}`, {})
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  apply(id: number) {
    return this.http
      .put<StocktakingV2Doc>(`${environment.BACKEND}/stocktaking-v2/apply/${id}`, {})
      .pipe(map((doc) => this.mapDoc(doc)));
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/stocktaking-v2/${id}`);
  }

  private mapListItem(item: StocktakingV2ListItem): StocktakingV2ListItem {
    return {
      ...item,
      date: new Date(item.date),
      date_created: new Date(item.date_created)
    };
  }

  private mapDoc(doc: StocktakingV2Doc): StocktakingV2Doc {
    return {
      ...doc,
      date: new Date(doc.date),
      date_created: new Date(doc.date_created),
      startedAt: new Date(doc.startedAt),
      movementBaselineAt: new Date(doc.movementBaselineAt),
      finalizedAt: doc.finalizedAt ? new Date(doc.finalizedAt) : null,
      appliedAt: doc.appliedAt ? new Date(doc.appliedAt) : null,
      participants: Array.isArray(doc.participants)
        ? doc.participants.map((participant) => ({
            ...participant,
            joinedAt: new Date(participant.joinedAt),
            lastSeenAt: new Date(participant.lastSeenAt)
          }))
        : [],
      shelves: Array.isArray(doc.shelves)
        ? doc.shelves.map((shelf) => ({
            ...shelf,
            lockAcquiredAt: shelf.lockAcquiredAt ? new Date(shelf.lockAcquiredAt) : null,
            lastHeartbeatAt: shelf.lastHeartbeatAt ? new Date(shelf.lastHeartbeatAt) : null,
            countedAt: shelf.countedAt ? new Date(shelf.countedAt) : null
          }))
        : []
    };
  }
}
